// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { CreditCardBillingAddressDto } from "@insite/client-framework/Types/ApiModels";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { CHECKOUT_PICKUP_DAY, CHECKOUT_WHEN_TO_SHIP } from "@werner/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { Draft } from "immer";
import merge from "lodash/merge";

interface AddressesPageState {
    oneTimeAddress?: ShipToModel;
    isSaveOneTimeAddress?: boolean;
    showMessageOfPricesChangeforOTA?: boolean;
    useOneTimeAddress?: boolean;
    isLoading?: boolean;
}

interface ShippingPageState {
    shippingMethod?: string;
    attentionTo?: string;
    contactPhoneNumber?: string;
    pickupDay?: string;
    shippingNotes?: string;
    pickupNotes?: string;
    isLoading?: boolean;
}

interface CreditCardBillingAddress {
    address1: string;
    country: string;
    state: string;
    city: string;
    postalCode: string;
}

interface PaymentPageState {
    creditCardBillingAddress?: CreditCardBillingAddressDto;
    isSavedPayment?: boolean;
    savedPaymentDescription?: string;
    poNumber?: string;
    firstSix?: string;
    lastFour?: string;
    cardType?: string;
    isCreditCard?: boolean;
    paymentMethod?: string;
    expirationDate?: string;
    customerReleaseNumber?: string;
    cardBillingAddress?: string;
    cardHolderName?: string;
    paymentMethodRadio?: string;
    promotionCode?: string;
    promotionCodeApplied?: boolean;
    hasNonAppliedPromotion?: boolean;
    isLoading?: boolean;
}

interface ShippingState {
    requestedDate?: Date;
}
interface PickupState {
    requestedDate?: Date;
}
interface FulfillmentState {
    shipping?: ShippingState;
    pickup?: PickupState;
}

export interface XCheckoutStateType {
    addresses: AddressesPageState;
    shipping: ShippingPageState;
    payment: PaymentPageState;
    fulfillment: FulfillmentState;
}

export const initialCheckoutState: XCheckoutStateType = {
    addresses: {},
    payment: {},
    shipping: {
        pickupDay: CHECKOUT_PICKUP_DAY.Today,
        shippingMethod: CHECKOUT_WHEN_TO_SHIP.ShipWhenAvailable,
    },
    fulfillment: {},
};

const reducer = {
    "Pages/CheckoutShipping/SetShippingAddress": (
        draft: Draft<XCheckoutStateType>,
        action: { address: ShipToModel },
    ) => {
        if (!action.address.oneTimeAddress) {
            return;
        }
        draft.addresses.oneTimeAddress = action.address;
    },
    "xCheckout/Update": (draft: Draft<XCheckoutStateType>, action: { data: Partial<XCheckoutStateType> }) => {
        draft = merge(draft, action.data);
    },
    "xCheckout/Reset": (draft: Draft<XCheckoutStateType>, action: { path?: keyof XCheckoutStateType }) => {
        if (!action.path) {
            draft.addresses = {};
            draft.fulfillment = {};
            draft.payment = {};
            draft.shipping = {
                pickupDay: CHECKOUT_PICKUP_DAY.Today,
                shippingMethod: CHECKOUT_WHEN_TO_SHIP.ShipWhenAvailable,
            };
            return;
        }
        draft[action.path] = initialCheckoutState[action.path];
    },
};

const xCheckout = createTypedReducerWithImmer(initialCheckoutState, reducer);

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: XCheckoutStateType;
} & ApplicationState;

type xCheckoutReducers = {
    [key: string]: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
